import { Dayjs } from 'dayjs'
import { request } from '../request'
import { HourlyStationVariableID } from '@models/station/HourlyStationVariable'
import { HourlyStationValues } from '@models/station/HourlyStationValues'
import { HistoricStationValue } from '@models/station/HistoricStationValue'
import { HistoricStationVariableID } from '@models/station/HistoricStationVariable'
import { HistoricStationValueDTO } from '@models/api/station/HistoricStationValueDTO'
import { AxiosResponse, HttpStatusCode } from 'axios'
import type { ObservationAemet as ServiceObservationAemet } from '@models/api/ObservationAemet'
import type { Synop as ServiceSynop } from '@models/api/Synop'
import { ObservationAemet } from '@models/ObservationAemet'
import { Synop } from '@models/Synop'
import { Station } from '@models/Station'
import { FileType } from '@enums/FileType'
import { APIError } from '@models/error/APIError'
import { ArrayHelper } from '@helpers/ArrayHelper'
import { StationListItemDTO } from '@models/api/station/Station'

export const stations = {
  getAllStations: function () {
    return request.get('stations')
  },

  getObservation: function (idStation: number) {
    return request.get(`stations/${idStation}`)
  },

  getObservationByDate: async function (idStation: number, date: Dayjs) {
    const result: AxiosResponse<ServiceObservationAemet | ServiceSynop> =
      await request.get(`stations/${idStation}`, {
        params: {
          date: date.toISOString()
        }
      })
    if (result.data) {
      if ('ID_OBSERVACIONES_AEMET' in result.data) {
        return new ObservationAemet(result.data)
      } else if ('ID_SYNOP' in result.data) {
        return ObservationAemet.fromSynop(new Synop(result.data))
      }
    }
  },

  getAllStationsHourlyData: async function (
    variable: HourlyStationVariableID,
    initialDate: Dayjs,
    finalDate: Dayjs,
    abortController: AbortController
  ) {
    const result = await request.get(`stations/station/all`, {
      params: {
        initDate: initialDate.toISOString(),
        endDate: finalDate?.toISOString(),
        variable
      },
      signal: abortController.signal
    })
    if (result.status !== HttpStatusCode.Ok) {
      throw new APIError('Error al obtener datos de estaciones', result.status)
    }

    const data: HourlyStationValues[] = result.data.map((item: any) =>
      HourlyStationValues.fromDTO(item, variable)
    )
    return ArrayHelper.toRecord(data, 'stationId')
  },

  getHistoricStationsMonthlyData: async function (
    variable: HistoricStationVariableID,
    monthDate: Dayjs,
    abortController: AbortController
  ): Promise<HistoricStationValue[]> {
    const result = await request.get(`stations/station/allMonthly`, {
      params: {
        variable,
        date: monthDate.format('YYYY-MM')
      },
      signal: abortController.signal
    })
    if (result.status !== HttpStatusCode.Ok) {
      throw new APIError('Error al obtener datos de estaciones', result.status)
    }
    return result.data.map((item: HistoricStationValueDTO) =>
      HistoricStationValue.fromDTO(item)
    )
  },

  getHistoricStationsDailyData: async function (
    variable: HistoricStationVariableID,
    date: Dayjs,
    abortController: AbortController
  ): Promise<HistoricStationValue[]> {
    const result = await request.get(`stations/station/allDaily`, {
      params: {
        variable,
        date: date.format('YYYY-MM-DD')
      },
      signal: abortController.signal
    })
    if (result.status !== HttpStatusCode.Ok) {
      throw new APIError('Error al obtener datos de estaciones', result.status)
    }
    return result.data.map((item: HistoricStationValueDTO) =>
      HistoricStationValue.fromDTO(item)
    )
  },

  downloadStationDailyData: function (
    idStation: Station['id'],
    date: Dayjs,
    format: FileType
  ) {
    return request.get(`stations/stationData/download`, {
      params: {
        idStation,
        initDate: date.utc().format('YYYY-MM-DD'),
        formatConv: format
      },
      responseType: 'blob'
    })
  },

  downloadStationData: function (
    idStation: Station['id'],
    startDate: Dayjs,
    format: FileType,
    endDate: Dayjs
  ) {
    return request.get(`stations/stationData/download`, {
      params: {
        idStation,
        initDate: startDate.toISOString(),
        formatConv: format,
        endDate: endDate.toISOString()
      },
      responseType: 'blob'
    })
  },

  getDefaultStations: function () {
    return request.get(`stations/station/defaultStations`)
  },

  getStationsByCCAA: function (codCCAA: number) {
    return request.get<StationListItemDTO[]>(
      `api/listStationsFromCCAA?cod_ccaa=${codCCAA}`
    )
  }
}
